'use strict';

// ─────────────────────────────────────────────────────────────────────────
// Tab-to-select for AntD AutoComplete / searchable-Select type-ahead inputs.
//
// UX (agreed): once the suggestion popup is open and the user has highlighted an
// option with ↓/↑, pressing Tab must COMMIT that highlighted MASTER item (the
// same as clicking it / pressing Enter) — never the raw typed text — and then
// let focus advance. If the popup is open but nothing is highlighted, Tab is
// blocked so the user can't skip past without picking a real item.
//
// This reads AntD's OWN active-option state from the DOM
// (`.ant-select-item-option-active`), so it always mirrors exactly what the user
// sees highlighted — no parallel index tracking that could drift out of sync.
//
//   Returns:
//     'selected' — a highlighted option was committed; caller advances focus.
//     'blocked'  — popup open with options but nothing highlighted; caller must
//                  swallow the Tab (preventDefault) so no free text slips through.
//     null       — not an open type-ahead (popup closed / empty / not a
//                  combobox); caller does its normal Tab behaviour, which keeps
//                  any free-typed SERVICE/OTHER text intact.
// ─────────────────────────────────────────────────────────────────────────

// The currently-OPEN AntD dropdown. Only one type-ahead popup is open at a time
// (the focused input's), so this reliably targets it. We do NOT scope by the
// input's aria-controls listbox id — AntD v5 renders options inside a virtual
// list whose nodes are not descendants of that listbox element, so scoping there
// finds zero options.
const OPEN_DD = '.ant-select-dropdown:not(.ant-select-dropdown-hidden)';

export function tabSelectActiveOption(inputEl) {
  if (!inputEl || typeof inputEl.getAttribute !== 'function') return null;
  if (inputEl.getAttribute('aria-expanded') !== 'true') return null;      // popup closed
  const options = document.querySelectorAll(`${OPEN_DD} .ant-select-item-option:not(.ant-select-item-option-disabled)`);
  if (!options || options.length === 0) return null;                      // open but empty → normal Tab
  const active = document.querySelector(`${OPEN_DD} .ant-select-item-option-active:not(.ant-select-item-option-disabled)`);
  if (!active) return 'blocked';                                          // nothing highlighted → block Tab

  // Commit exactly as a mouse pick would: rc-select preventDefaults mousedown
  // (to keep focus) and selects on click, firing the component's onSelect.
  try {
    active.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
    active.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
  } catch (_e) { /* older browsers — click() below still fires selection */ }
  active.click();
  return 'selected';
}

// The next tab-order element after `fromEl` in the document (skips hidden and
// tabindex=-1). Used to advance focus ourselves — AntD's post-select focus
// management can eat a plain native Tab.
const TABBABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
function _focusNextTabbable(fromEl) {
  const all = Array.from(document.querySelectorAll(TABBABLE))
    .filter((el) => el === fromEl || (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0));
  const i = all.indexOf(fromEl);
  if (i >= 0 && i + 1 < all.length) {
    const n = all[i + 1];
    try { n.focus({ preventScroll: true }); } catch (_e) { n.focus(); }
    if (n.select) { try { n.select(); } catch (_e) { /* noop */ } }
  }
}

// Convenience for STANDALONE AntD AutoComplete/Select (outside the proforma grid):
// use as `onInputKeyDown={makeInputKeyDownTabSelect()}`. On Tab it commits the
// highlighted option and advances to the next field; if nothing is highlighted it
// blocks Tab so no free text slips through. Other keys pass through untouched.
export function makeInputKeyDownTabSelect(extra) {
  return (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const res = tabSelectActiveOption(e.target);
      if (res === 'blocked') { e.preventDefault(); e.stopPropagation(); return; }
      if (res === 'selected') {
        e.preventDefault(); e.stopPropagation();
        const from = e.target;
        const go = () => { try { if (from && from.blur) from.blur(); } catch (_e) { /* noop */ } _focusNextTabbable(from); };
        requestAnimationFrame(go);
        setTimeout(go, 60);
        return;
      }
    }
    if (typeof extra === 'function') extra(e);
  };
}
